import { type ReactNode } from 'react';

import type { themeVars } from '@shieldpay/theme-provider-ui/theme/contract';
import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import { type ActionMenuProps, ActionMenu } from '../action-menu/action-menu';
import { Text } from '../text/text';

import * as styles from './table-cell.css';

export type CellTextVariantType = keyof Pick<
  typeof themeVars.typography.variant,
  'caption150' | 'captionMedium150'
>;

export interface TableDataCellProps {
  /**
   * Additional classes to apply.
   */
  className?: string;
  /**
   * Children text/nodes.
   */
  children?: ReactNode;
  /**
   * When the header applies to multiple columns.
   */
  colSpan?: number;
  /**
   * When the header applies to multiple rows.
   **/
  rowSpan?: number;
  /**
   * Aligning value based on the value type. E.g. Left align text values and right align number values.
   */
  align?: keyof typeof styles.textAlign;
  /**
   *  Set the text variant for this cell. This is `caption150` by default
   */
  cellTextVariant?: CellTextVariantType;
  /**
   * By default values are wrapped in a text component. This can be overridden by setting this value to false
   *
   */
  textWrapper?: boolean;
  truncate?: boolean;
  /**
   * Pins the cell - pixel value for the left position
   * 'auto' is a special value used on the initial render to apply partial pinning styles
   */
  pinnedLeft?: number | 'auto';
  /**
   * To help defines the style of the  cell.
   **/
  variant?: 'default' | 'shrink';
  maxWidth?: number;
}

export const TableDataCell = ({
  children,
  colSpan = 1,
  rowSpan = 1,
  className,
  align = 'left',
  cellTextVariant = 'caption150',
  textWrapper = true,
  maxWidth,
  truncate = false,
  pinnedLeft,
  variant = 'default',
}: TableDataCellProps) => (
  <td
    colSpan={colSpan}
    rowSpan={rowSpan}
    className={classFilter([
      styles.base,
      variant === 'shrink' && styles.shrink,
      styles.textAlign[align],
      className,
      (typeof pinnedLeft === 'number' || pinnedLeft === 'auto') &&
        styles.cellPinned,
    ])}
    style={{
      left: pinnedLeft,
      maxWidth,
    }}
  >
    {textWrapper ? (
      <Text
        variant={cellTextVariant}
        truncate={truncate}
        className={truncate ? styles.textTruncate : undefined}
      >
        {children}
      </Text>
    ) : (
      children
    )}
  </td>
);

export const TableActionCell = ({
  children,
  className,
  align = 'left',
  triggerLabel,
  disabled,
}: TableDataCellProps & ActionMenuProps) => {
  return (
    <td
      colSpan={1}
      rowSpan={1}
      className={classFilter([
        styles.action,
        styles.textAlign[align],
        className,
      ])}
    >
      <ActionMenu triggerLabel={triggerLabel} disabled={disabled}>
        {children}
      </ActionMenu>
    </td>
  );
};
